import type { Thread, ThreadWatcher, ThreadWatchers as Watchers, WatchRequest } from '@central/api';
import { Alert, Button, Select } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CSSProperties } from 'react';
import { keys, useApi } from '../api';
import { errorMessage } from '../lib/errors';
import { muted } from '../lib/labels';

type Side = WatchRequest['side'];

/**
 * Who gets conversation emails about a ticket beside the defaults: the
 * caller's own Watch toggle, staff watchers, and customer watchers, which
 * staff may add from the customer's users and remove.
 */
export function ThreadWatchers({ t }: { t: Thread }) {
  const api = useApi();
  const qc = useQueryClient();
  const w = useQuery({
    queryKey: keys.threadWatchers(t.id),
    queryFn: () => api.threads.watchers(t.id),
    retry: false,
  });
  const users = useQuery({
    queryKey: keys.customerUsers(t.customer.id),
    queryFn: () => api.customers.users(t.customer.id),
    staleTime: 60_000,
  });
  const change = useMutation({
    mutationFn: ({ side, userId, on }: { side: Side; userId: string; on: boolean }) =>
      on ? api.threads.watch(t.id, { side, userId }) : api.threads.unwatch(t.id, side, userId),
    onSuccess: (d) => qc.setQueryData<Watchers>(keys.threadWatchers(t.id), d),
  });

  if (w.error) {
    return <p style={{ margin: 0, fontSize: 13, ...muted }}>Couldn’t load the watchers.</p>;
  }
  const data = w.data;
  if (!data) return null;
  const listed = new Set(data.customer.watchers.map((x) => x.id));
  const candidates = (users.data?.users ?? []).filter(
    (u) => u.enabled && !listed.has(u.id) && u.id !== data.customer.reporter?.id,
  );
  const remove = (side: Side) => (p: ThreadWatcher) =>
    change.mutate({ side, userId: p.id, on: false });

  return (
    <section aria-label="Watchers" style={box}>
      <div>
        <Button
          size="xs"
          variant="default"
          loading={change.isPending}
          onClick={() => change.mutate({ side: 'staff', userId: 'me', on: !data.watching })}
        >
          {data.watching ? 'Stop watching' : 'Watch'}
        </Button>
      </div>
      <Side
        label="Rethink"
        base={t.assignee ? `${t.assignee.name} · assignee` : 'Every staff member · unassigned'}
        side={data.staff}
        onRemove={remove('staff')}
      />
      <Side
        label={t.customer.name}
        side={data.customer}
        everyone={data.everyone}
        onRemove={remove('customer')}
      />
      {candidates.length > 0 && (
        <Select
          aria-label="Add a customer watcher"
          placeholder={`Add someone at ${t.customer.name}`}
          size="xs"
          searchable
          value={null}
          data={candidates.map((u) => ({ value: u.id, label: `${u.name} (${u.email})` }))}
          onChange={(v) => v && change.mutate({ side: 'customer', userId: v, on: true })}
        />
      )}
      {change.error && <Alert color="red">{errorMessage(change.error)}</Alert>}
    </section>
  );
}

const box: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

/**
 * One side's recipients: base names who is mailed regardless of watchers
 * (staff: the assignee or everyone); with everyone set (customer side, no
 * watchers) every user is, so nobody is listed.
 */
function Side({
  label,
  base,
  side,
  everyone,
  onRemove,
}: {
  label: string;
  base?: string;
  side: Watchers['staff'];
  everyone?: boolean;
  onRemove: (p: ThreadWatcher) => void;
}) {
  const others = side.watchers.filter((p) => p.id !== side.reporter?.id);
  return (
    <div style={{ fontSize: 13 }}>
      <div style={{ ...muted, fontSize: 12 }}>{label}</div>
      <ul aria-label={`${label} watchers`} style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {base && <li style={muted}>{base}</li>}
        {everyone ? (
          <li style={muted}>Everyone with conversation emails on</li>
        ) : (
          <>
            {side.reporter && <Person p={side.reporter} note="reporter" />}
            {others.map((p) => (
              <Person key={p.id} p={p} onRemove={() => onRemove(p)} />
            ))}
          </>
        )}
      </ul>
    </div>
  );
}

function Person({ p, note, onRemove }: { p: ThreadWatcher; note?: string; onRemove?: () => void }) {
  return (
    <li style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span>
        {p.name}
        {note && <span style={muted}> · {note}</span>}
        {!p.receivesEmail && <span style={muted}> · emails off</span>}
      </span>
      {onRemove && (
        <Button
          size="compact-xs"
          variant="subtle"
          aria-label={`Remove ${p.name}`}
          onClick={onRemove}
        >
          Remove
        </Button>
      )}
    </li>
  );
}
